import React, { useEffect, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import BranchForm from './BranchForm';
import { RootState } from '../Redux/store';
import { Modal, Button, Pagination, Form } from 'react-bootstrap';

interface Branch {
  id: number;
  name: string;
  address: string;
  contact_number: string;
  manager: string;
  logo?: string; // Optional logo field
}

const BranchList: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const [totalPages, setTotalPages] = useState(1); // Total pages for pagination
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const itemsPerPage = 12; // Number of branches per page

  // Fetch branches from the API
  const fetchBranches = async (page: number = 1, search: string = '') => {
    try {
      const response = await axios.get('https://api.sominnovations.xyz/Branches/branches/', {
        params: {
          page: page,
          search: search,
        },
      });
      console.log('API Response:', response.data); // Log the response

      // Extract the `results` array and total count from the response
      if (response.data && Array.isArray(response.data.results)) {
        setBranches(response.data.results);
        setTotalPages(Math.ceil(response.data.count / itemsPerPage));
      } else {
        setError('Invalid API response format.');
      }
    } catch (error) {
      setError('Failed to fetch branches.');
      console.error('Error fetching branches:', error);
    }
  };

  // Fetch branches on component mount or when page/search changes
  useEffect(() => {
    fetchBranches(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  // Handle branch deletion
  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this branch?');
    if (confirmDelete) {
      try {
        await axios.delete(`https://api.sominnovations.xyz/Branches/branches/${id}/`);
        setSuccess('Branch deleted successfully');
        fetchBranches(currentPage, searchQuery); // Refresh the branch list
      } catch (error) {
        setError('Failed to delete branch.');
      }
    }
  };

  // Handle branch edit
  const handleEdit = (branch: Branch) => {
    setEditBranch(branch);
    setShowModal(true); // Open the modal
  };

  // Handle successful update
  const handleSuccess = () => {
    setShowModal(false); // Close the modal
    fetchBranches(currentPage, searchQuery); // Refresh the branch list
    setSuccess('Branch updated successfully');
  };

  // Close the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditBranch(null);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  return (
    <div style={{ display: 'flex', overflowX: 'hidden' }}> {/* Flexbox layout */}
      {/* Sidebar */}
      <div style={{ width: '200px', backgroundColor: '#f8f9fa', padding: '10px' }}>
        <h4>Sidebar</h4>
        {/* Sidebar content goes here */}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '10px', overflowX: 'hidden' }}> {/* Prevent horizontal scroll */}
        {success && <div className="alert alert-success mt-3">{success}</div>}
        {error && <div className="alert alert-danger mt-3">{error}</div>}

        {/* Search Input */}
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search by branch name..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ maxWidth: '300px' }} // Smaller search input
          />
        </Form.Group>

        {/* Card Layout */}
        <div className="row">
          {Array.isArray(branches) &&
            branches.map((branch) => (
              <div key={branch.id} className="col-md-2 mb-3"> {/* Smaller column size */}
                <div className="card h-100" style={{ fontSize: '0.7rem', padding: '5px' }}> {/* Smaller font size and padding */}
                  {/* Branch Logo */}
                  {branch.logo && (
                    <img
                      src={branch.logo}
                      alt={`${branch.name} logo`}
                      className="card-img-top"
                      style={{ height: '50px', objectFit: 'cover' }} // Smaller logo
                    />
                  )}
                  <div className="card-body" style={{ padding: '5px' }}> {/* Smaller padding */}
                    {/* Branch Name */}
                    <h6 className="card-title" style={{ fontSize: '0.8rem', marginBottom: '3px' }}>{branch.name}</h6> {/* Smaller title */}
                    {/* Branch Details */}
                    <p className="card-text" style={{ marginBottom: '3px' }}>
                      <strong>Address:</strong> {branch.address}
                      <br />
                      <strong>Contact:</strong> {branch.contact_number}
                      <br />
                      <strong>Manager:</strong> {branch.manager}
                    </p>
                      <strong>ID:</strong> {branch.id}
                    {/* Actions */}
                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleEdit(branch)}
                        style={{ fontSize: '0.6rem', padding: '1px 3px' }} // Smaller button
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(branch.id)}
                        style={{ fontSize: '0.6rem', padding: '1px 3px' }} // Smaller button
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            {Array.from({ length: totalPages }, (_, index) => (
              <Pagination.Item
                key={index + 1}
                active={index + 1 === currentPage}
                onClick={() => handlePageChange(index + 1)}
                style={{ fontSize: '0.7rem', padding: '1px 3px' }} // Smaller pagination
              >
                {index + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>

        {/* Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="sm"> {/* Smaller modal */}
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '0.9rem' }}>Edit Branch</Modal.Title> {/* Smaller title */}
          </Modal.Header>
          <Modal.Body>
            {editBranch && <BranchForm branch={editBranch} onSuccess={handleSuccess} />}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} style={{ fontSize: '0.7rem', padding: '1px 3px' }}> {/* Smaller button */}
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default BranchList;